<script setup lang="ts">
import { computed } from 'vue';
import StatusHeader from '@/components/StatusHeader.vue';
import ComponentGroupCard from '@/components/ComponentGroupCard.vue';
import IncidentList from '@/components/IncidentList.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import { useStatus } from '@/composables/useStatus';
import { useConfig } from '@/composables/useConfig';
import { statusMessages } from '@/i18n';

const { snapshot, loading, error, lastUpdated, refresh } = useStatus();
const config = useConfig();

const activeIncidents = computed(() =>
  (snapshot.value?.incidents ?? []).filter((i) => i.status !== 'resolved'),
);

const resolvedIncidents = computed(() =>
  (snapshot.value?.incidents ?? []).filter((i) => i.status === 'resolved').slice(0, 10),
);

// The collector publishes the window it actually collected; the runtime config
// is only the fallback for snapshots written before that field existed.
const uptimeWindowDays = computed(
  () => snapshot.value?.historyWindowDays ?? config.value.uptimeWindowDays,
);
</script>

<template>
  <main class="page">
    <header class="page-header">
      <h1>{{ config.brand.name }}</h1>
      <p class="tagline">{{ config.brand.tagline }}</p>
    </header>

    <StatusHeader
      :snapshot="snapshot"
      :loading="loading"
      :error="error"
      :last-updated="lastUpdated"
      @refresh="refresh"
    />

    <IncidentList
      v-if="activeIncidents.length > 0"
      :incidents="activeIncidents"
      :heading="statusMessages.activeIncidents"
      :show-empty-state="false"
    />

    <section v-if="snapshot" class="groups">
      <ComponentGroupCard
        v-for="group in snapshot.groups"
        :key="group.id"
        :group="group"
        :snapshot="snapshot"
        :uptime-window-days="uptimeWindowDays"
      />
    </section>

    <IncidentList
      v-if="snapshot"
      :incidents="resolvedIncidents"
      :heading="statusMessages.incidentHistory"
      :show-empty-state="true"
    />

    <SiteFooter :last-updated="lastUpdated" />
  </main>
</template>

<style scoped>
.page {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1.25rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  text-align: left;
  margin-bottom: 0.5rem;
}

.page-header h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: var(--xt-fg-strong);
  letter-spacing: -0.01em;
}

.tagline {
  margin: 0.25rem 0 0;
  color: var(--xt-fg-muted);
  font-size: 0.95rem;
}

.groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
